import { DocumentNode, OperationVariables, useLazyQuery } from '@apollo/client'
import debounce from 'lodash/debounce'
import { useEffect, useRef, useState } from 'react'

type DebouncedQueryOptions<TData, TResultItem, TVariables extends OperationVariables> = {
  query: DocumentNode
  transformData: (data: TData) => TResultItem[]
  variables?: TVariables
  minChars?: number
  debounceTime?: number
  buildSearchVariables?: (searchText: string, baseVariables?: TVariables) => TVariables
}

export function useDebouncedQuery<TData, TResultItem, TVariables extends OperationVariables>({
  query,
  transformData,
  variables,
  minChars = 3,
  debounceTime = 300,
  buildSearchVariables
}: DebouncedQueryOptions<TData, TResultItem, TVariables>) {
  const [results, setResults] = useState<TResultItem[]>([])

  const [executeQuery, { loading }] = useLazyQuery<TData, TVariables>(query, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data) {
        const collection = transformData(data)
        setResults(collection)
      } else {
        setResults([])
      }
    }
  })

  const debouncedSearch = useRef(
    debounce((searchText: string) => {
      if (searchText.length >= minChars) {
        const searchVariables = buildSearchVariables
          ? buildSearchVariables(searchText, variables)
          : variables

        executeQuery({
          variables: searchVariables
        })
      } else {
        setResults([])
      }
    }, debounceTime)
  ).current

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  return { results, loading, debouncedSearch, setResults }
}
